// iot-admin/client/src/pages/LoopHealth.js
//
// Sprint-D9 G.5 — Loop-health admin tile.
// 4-tile read-only summary of the commercial loop's last-24h activity
// + the founder-loop's latest tier_t pin + cube version. Polls every
// 30s. Lights up immediately after a B.6 synthetic-loop run.

import React, { useEffect, useState } from 'react';
import api from '../services/api';  // axios instance (baseURL = /api)

const POLL_MS = 30_000;

function Tile({ label, count, sub, stage }) {
  const isWired = count > 0;
  return (
    <div className="stat-card" style={{ position: 'relative' }}>
      <h3>
        <span style={{ opacity: 0.55, marginRight: 6 }}>Stage {stage}</span>
        {label}
      </h3>
      <div className="value" style={{ color: isWired ? '#16a34a' : '#9ca3af' }}>
        {count}
      </div>
      {sub && (
        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 4 }}>
          {Object.entries(sub).map(([k, v]) => (
            <span key={k} style={{ marginRight: 12 }}>
              {k.replace(/_/g, ' ')}: <strong>{v}</strong>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FounderLoopStrip({ tierT, cube }) {
  return (
    <div className="section" style={{ marginTop: 24 }}>
      <div className="section-header"><h2>Founder loop (Tier-T + cube)</h2></div>
      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Layer</th>
              <th>Latest</th>
              <th>Source window end (UTC)</th>
              <th>URI / fingerprint</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>tier_t_v1 pin</td>
              <td>{tierT.available ? `v${String(tierT.pinSeq).padStart(5, '0')}` : '—'}</td>
              <td>{tierT.available ? tierT.pinSourceWindowEndUtc : tierT.error || '—'}</td>
              <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {tierT.available ? tierT.pinUri : '—'}
              </td>
            </tr>
            <tr>
              <td>cube</td>
              <td>{cube.available ? cube.version : '—'}</td>
              <td>{cube.available ? cube.publishUtc : cube.error || '—'}</td>
              <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {cube.available
                  ? `fp=${(cube.schemaFingerprint || '').substring(0, 16)}… clusters_with_tier_t=${cube.nClustersWithTierT ?? 0}`
                  : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function LoopHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(24);

  const fetchOnce = async (windowHours) => {
    try {
      const res = await api.get('/admin/loop-health', { params: { hours: windowHours } });
      setData(res.data?.data || null);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Failed to load loop health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnce(hours);
    const id = setInterval(() => fetchOnce(hours), POLL_MS);
    return () => clearInterval(id);
  }, [hours]);

  if (loading && !data) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Loop health</h1>
        <span className="last-updated">
          Window: last {hours}h · computed {data ? new Date(data.computedAt).toLocaleTimeString() : '—'}
        </span>
      </div>

      {error && (
        <div style={{ color: '#dc2626', padding: 8, marginBottom: 16 }}>
          Error: {error}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        Window:&nbsp;
        {[1, 6, 24, 168].map((h) => (
          <button
            key={h}
            className={`btn ${hours === h ? 'btn-primary' : 'btn-secondary'}`}
            style={{ marginRight: 8 }}
            onClick={() => setHours(h)}
          >
            {h < 24 ? `${h}h` : `${h / 24}d`}
          </button>
        ))}
      </div>

      <div className="dashboard-grid">
        {data?.tiles?.map((t) => (
          <Tile key={t.key} {...t} />
        ))}
      </div>

      {data?.founderLoop && (
        <FounderLoopStrip
          tierT={data.founderLoop.tierT}
          cube={data.founderLoop.cube}
        />
      )}
    </div>
  );
}
